import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { fecharComEsc, imprimirFechamentoCaixaTermico, imprimirComprovanteMovimentacao } from '../utils/impressaoVenda';
import { hojeBR } from '../utils/data';
import s from './Financeiro.module.css';
import InputDataFiltro from '../components/common/InputDataFiltro';

export default function FechamentoCaixa() {
  const [turnoAberto, setTurnoAberto] = useState(null);
  const [contas, setContas]           = useState([]);
  const [historico, setHistorico]     = useState([]);
  const [resumo, setResumo]           = useState(null);
  const [turnoSel, setTurnoSel]       = useState(null);
  const [modoResumo, setModoResumo]   = useState('resumido');
  const [aba, setAba]                 = useState('turno');
  const [larguraTermica, setLarguraTermica] = useState('80mm');

  // Abertura
  const [contaAbertura, setContaAbertura] = useState('');
  const [saldoAbertura, setSaldoAbertura] = useState('0');

  // Fechamento
  const [saldoFechamento, setSaldoFechamento] = useState('');
  const [obsFechar, setObsFechar]             = useState('');

  // Formas do turno aberto (para cálculo em tempo real)
  const [formasAtual, setFormasAtual]         = useState([]);
  const [totaisAtual, setTotaisAtual]         = useState(null);
  const [movsAtual, setMovsAtual]             = useState([]);

  const [carregando, setCarregando] = useState(false);
  const [erro, setErro]             = useState('');
  const [sucesso, setSucesso]       = useState('');
  const [histDe, setHistDe]         = useState(hojeBR());
  const [histAte, setHistAte]       = useState(hojeBR());

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [rt, rc, rh] = await Promise.all([
        api.get('/financeiro/turnos/aberto'),
        api.get('/financeiro/contas'),
        api.get('/financeiro/turnos', { params: { de: histDe || undefined, ate: histAte || undefined } }),
      ]);
      setTurnoAberto(rt.data);
      setContas(rc.data.filter(c => c.ativo && c.tipo === 'caixa'));
      setHistorico(rh.data);
    } catch { setErro('Erro ao carregar dados.'); }
    finally { setCarregando(false); }
  }, []); // eslint-disable-line -- só a carga inicial (hoje); refiltrar é via botão Filtrar

  useEffect(() => { carregar(); }, [carregar]);

  // Carrega formas do turno aberto para cálculo em tempo real
  useEffect(() => {
    if (!turnoAberto) { setFormasAtual([]); setMovsAtual([]); return; }
    api.get(`/financeiro/turnos/${turnoAberto.id}/resumo`)
      .then(({ data }) => {
        setFormasAtual(data.formas || []);
        setTotaisAtual(data.totais || null);
        setMovsAtual(data.movs || []);
      })
      .catch(() => { setFormasAtual([]); setTotaisAtual(null); setMovsAtual([]); });
  }, [turnoAberto]);

  // ── Cálculos derivados (turno aberto) ───────────────────────
  const f = v => parseFloat(v || 0);

  // Efeito das movimentações do turno sobre o caixa (sangrias, suprimentos,
  // reembolsos de devolução...). Vendas/recebimentos ficam de fora — já contam
  // no card "Vendas em Dinheiro". O suprimento/sangria AUTOMÁTICO gerado pelo
  // parâmetro "Realizar sangria/suprimentos pelo PDV" (turno_saldo_id) também
  // fica de fora — o valor dele já está em saldo_abertura/saldo_fechamento,
  // contá-lo aqui de novo duplicaria o valor no Saldo Final.
  const ajusteMovsTurno = (movs, contaId) => (movs || []).reduce((acc, m) => {
    if (m.tipo === 'venda' || m.tipo === 'recebimento' || m.turno_saldo_id) return acc;
    const v = f(m.valor);
    if (contaId) {
      if (m.conta_destino_id === contaId) return acc + v;
      if (m.conta_origem_id  === contaId) return acc - v;
      return acc;
    }
    if (['suprimento', 'entrada'].includes(m.tipo)) return acc + v;
    if (['sangria', 'saida', 'devolucao'].includes(m.tipo)) return acc - v;
    return acc;
  }, 0);

  // vp.valor (o que soma "dinheiro_bruto_*") já é o LÍQUIDO — o troco do
  // pagamento em dinheiro já foi descontado no backend na hora da venda
  // (trocoRestante, vendasController.criar). Nunca subtrair troco_* de novo
  // aqui: já fez isso subestimar o dinheiro esperado em caixa (e por
  // consequência marcar "sobra"/"falta" falsas no fechamento).
  const dinheiro_bruto_atual = f(
    formasAtual.find(x => x.forma_pagamento?.toLowerCase() === 'dinheiro')?.total
  );
  const troco_atual    = f(totaisAtual?.total_troco);
  const dinheiro_atual = dinheiro_bruto_atual;
  const movs_atual_ajuste = ajusteMovsTurno(movsAtual, turnoAberto?.conta_id);
  const valor_em_caixa = f(turnoAberto?.saldo_abertura) + dinheiro_atual + movs_atual_ajuste;
  const saldo_fech_num = saldoFechamento !== '' ? f(saldoFechamento) : null;
  const diferenca_atual = saldo_fech_num !== null ? saldo_fech_num - valor_em_caixa : null;

  // ── Cálculos derivados (resumo histórico) ───────────────────
  const dinheiro_bruto_resumo = resumo
    ? f(resumo.formas.find(x => x.forma_pagamento?.toLowerCase() === 'dinheiro')?.total)
    : 0;
  const troco_resumo   = f(resumo?.totais?.total_troco);
  const dinheiro_resumo = dinheiro_bruto_resumo;
  const movs_resumo_ajuste = resumo ? ajusteMovsTurno(resumo.movs, resumo.turno.conta_id) : 0;
  const valor_em_caixa_resumo = resumo
    ? f(resumo.turno.saldo_abertura) + dinheiro_resumo + movs_resumo_ajuste
    : 0;
  const diferenca_resumo = resumo && resumo.turno.saldo_fechamento != null
    ? f(resumo.turno.saldo_fechamento) - valor_em_caixa_resumo
    : null;

  const abrirTurno = async () => {
    setErro('');
    try {
      await api.post('/financeiro/turnos/abrir', {
        conta_id:       contaAbertura ? parseInt(contaAbertura) : null,
        saldo_abertura: parseFloat(saldoAbertura) || 0,
      });
      setSucesso('Caixa aberto!');
      setTimeout(() => setSucesso(''), 2000);
      await carregar();
    } catch (err) { setErro(err.response?.data?.erro || 'Erro ao abrir caixa.'); }
  };

  const fecharTurno = async () => {
    if (saldoFechamento === '') { setErro('Informe o saldo de fechamento.'); return; }
    setErro('');
    try {
      await api.put(`/financeiro/turnos/${turnoAberto.id}/fechar`, {
        saldo_fechamento: parseFloat(saldoFechamento),
        observacoes: obsFechar || null,
      });
      setSucesso('Caixa fechado com sucesso!');
      setTimeout(() => setSucesso(''), 2500);
      setTurnoAberto(null);
      await carregar();
    } catch (err) { setErro(err.response?.data?.erro || 'Erro ao fechar caixa.'); }
  };

  const verResumo = async (t) => {
    setResumo(null); setTurnoSel(t);
    try {
      const { data } = await api.get(`/financeiro/turnos/${t.id}/resumo`);
      setResumo(data);
      setAba('historico');
    } catch { setErro('Erro ao carregar resumo.'); }
  };

  const fmtBRL = v => parseFloat(v||0).toLocaleString('pt-BR',{minimumFractionDigits:2,maximumFractionDigits:2});
  const fmtDT  = d => new Date(d).toLocaleString('pt-BR',{dateStyle:'short',timeStyle:'short'});

  const corDiferenca = d => d == null ? '#555' : d > 0.005 ? '#065f46' : d < -0.005 ? '#b91c1c' : '#166534';
  const bgDiferenca  = d => d == null ? '#f5f5f5' : d > 0.005 ? '#dcfce7' : d < -0.005 ? '#fee2e2' : '#dcfce7';

  const imprimirResumo = () => {
    if (!resumo) return;
    const { turno, formas, totais, movs, vendas } = resumo;

    const difLabel = diferenca_resumo == null ? ''
      : Math.abs(diferenca_resumo) < 0.005 ? '✔ conferido'
      : diferenca_resumo > 0 ? '▲ sobra' : '▼ falta';
    const difCor = diferenca_resumo == null ? '#555'
      : diferenca_resumo > 0.005 ? '#065f46'
      : diferenca_resumo < -0.005 ? '#b91c1c' : '#166534';
    const difBg = diferenca_resumo == null ? '#f5f5f5'
      : diferenca_resumo > 0.005 ? '#dcfce7'
      : diferenca_resumo < -0.005 ? '#fee2e2' : '#dcfce7';

    const cardHtml = (label, value, cor = '#1e3a5f', bold = false) => `
      <div style="background:#f0f4fa;border:1px solid #c5d5e8;border-radius:6px;
                  padding:10px 14px;text-align:center;flex:1;min-width:130px;">
        <div style="font-size:10pt;color:#64748b;margin-bottom:4px;">${label}</div>
        <div style="font-size:15pt;font-weight:${bold?'bold':'600'};color:${cor};">R$ ${fmtBRL(value)}</div>
      </div>`;

    const thStyle = 'padding:6px 8px;background:#1e3a5f;color:#fff;font-size:11pt;text-align:left;';
    const tdStyle = 'padding:5px 8px;font-size:11pt;border-bottom:1px solid #e5e7eb;';
    const tdRight = tdStyle + 'text-align:right;';

    const linhasFormas = formas.map(f => {
      const isDin   = f.forma_pagamento?.toLowerCase() === 'dinheiro';
      // f.total (dinheiro) já é líquido — soma de volta o troco só pra exibir
      // o bruto recebido nesta linha, coerente com a linha "(−) Troco" abaixo.
      const totalLiq = isDin ? parseFloat(f.total) + troco_resumo : parseFloat(f.total);
      const trocoRow = isDin && troco_resumo > 0
        ? `<tr style="color:#b45309;">
             <td style="${tdStyle};padding-left:20px;" colspan="3">(−) Troco concedido</td>
             <td style="${tdRight}">R$ ${fmtBRL(troco_resumo)}</td>
           </tr>` : '';
      return `<tr style="${isDin ? 'background:#f0fdf4;font-weight:bold;' : ''}">
        <td style="${tdStyle}">${f.forma_pagamento}</td>
        <td style="${tdStyle}">${f.bandeira || '—'}</td>
        <td style="${tdRight.replace('text-align:right','text-align:center')}">${f.qtd}</td>
        <td style="${tdRight}">R$ ${fmtBRL(totalLiq)}</td>
      </tr>${trocoRow}`;
    }).join('');

    const linhasMovs = movs.length === 0 ? '' : `
      <div style="margin-top:16px;">
        <div style="font-size:12pt;font-weight:bold;color:#1e3a5f;border-bottom:2px solid #1e3a5f;
                    padding-bottom:4px;margin-bottom:8px;">Movimentações do Turno</div>
        <table style="width:100%;border-collapse:collapse;">
          <thead><tr>
            <th style="${thStyle}">Tipo</th>
            <th style="${thStyle}">Origem</th>
            <th style="${thStyle}">Destino</th>
            <th style="${thStyle}text-align:right;">Valor</th>
            <th style="${thStyle}">Descrição</th>
          </tr></thead>
          <tbody>
            ${movs.map(m => `<tr>
              <td style="${tdStyle}">${m.tipo}</td>
              <td style="${tdStyle}">${m.conta_origem_nome || '—'}</td>
              <td style="${tdStyle}">${m.conta_destino_nome || '—'}</td>
              <td style="${tdRight}">R$ ${fmtBRL(m.valor)}</td>
              <td style="${tdStyle}">${m.descricao || '—'}</td>
            </tr>`).join('')}
          </tbody>
        </table>
      </div>`;

    // Agrupa vendas por forma de pagamento (uma venda pode aparecer em mais de um grupo)
    const gruposImpressao = (() => {
      const map = {};
      vendas.forEach(v => {
        (v.pagamentos || []).forEach(p => {
          const key = `${p.forma}${p.bandeira ? ` (${p.bandeira})` : ''}`;
          if (!map[key]) map[key] = { label: key, itens: [], total: 0 };
          map[key].itens.push({ v, p });
          map[key].total += parseFloat(p.valor || 0);
        });
      });
      return Object.values(map);
    })();

    const thLeve = 'padding:5px 8px;background:#f3f4f6;color:#374151;font-size:11pt;text-align:left;border-bottom:2px solid #d1d5db;';

    const linhasVendas = modoResumo !== 'detalhado' || vendas.length === 0 ? '' :
      `<div style="margin-top:16px;">
        <div style="font-size:12pt;font-weight:bold;color:#374151;border-bottom:2px solid #374151;
                    padding-bottom:4px;margin-bottom:12px;">Vendas Detalhadas por Forma de Pagamento</div>
        ${gruposImpressao.map(g => `
          <div style="margin-bottom:16px;page-break-inside:avoid;">
            <div style="display:flex;justify-content:space-between;align-items:baseline;
                        border-bottom:1px solid #d1d5db;padding-bottom:3px;margin-bottom:5px;">
              <span style="font-size:11pt;font-weight:bold;color:#374151;">${g.label}</span>
              <span style="font-size:10pt;color:#6b7280;">${g.itens.length} venda(s)</span>
            </div>
            <table style="width:100%;border-collapse:collapse;table-layout:fixed;">
              <colgroup>
                <col style="width:34px;"><col style="width:78px;"><col style="width:18%;">
                <col style="width:12%;"><col style="width:20%;"><col style="width:70px;"><col style="width:75px;">
              </colgroup>
              <thead><tr>
                <th style="${thLeve}">#</th>
                <th style="${thLeve}">Data/Hora</th>
                <th style="${thLeve}">Cliente</th>
                <th style="${thLeve}">Vendedor</th>
                <th style="${thLeve}">Obs</th>
                <th style="${thLeve}text-align:right;">Desconto</th>
                <th style="${thLeve}text-align:right;">Valor</th>
              </tr></thead>
              <tbody>
                ${g.itens.map(({ v, p }) => `<tr>
                  <td style="${tdStyle}">${v.id}</td>
                  <td style="${tdStyle}">${fmtDT(v.criado_em)}</td>
                  <td style="${tdStyle}overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${v.cliente_nome || 'Consumidor Final'}</td>
                  <td style="${tdStyle}overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${v.vendedor_nome || '—'}</td>
                  <td style="${tdStyle}overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#6b7280;">${v.observacoes || '—'}</td>
                  <td style="${tdRight};color:#b91c1c;">
                    ${parseFloat(v.desconto) > 0 ? `R$ ${fmtBRL(v.desconto)}` : '—'}
                  </td>
                  <td style="${tdRight};font-weight:bold;">R$ ${fmtBRL(p.valor)}</td>
                </tr>`).join('')}
              </tbody>
              <tfoot>
                <tr style="border-top:1px solid #d1d5db;">
                  <td style="padding:4px 8px;font-size:11pt;font-weight:bold;color:#374151;" colspan="6">
                    Total ${g.label}
                  </td>
                  <td style="${tdRight};font-weight:bold;font-size:12pt;color:#111827;">
                    R$ ${fmtBRL(g.total)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>`).join('')}
      </div>`;

    const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="utf-8">
  <title>Conferência de Caixa — Turno #${turno.id}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    @page { size: A4; margin: 14mm 12mm; }
    body { font-family: Arial, sans-serif; font-size: 12pt; color: #1a1a1a; background: #fff; }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  </style>
</head>
<body>
  <!-- Cabeçalho -->
  <div style="border-bottom:3px solid #1e3a5f;padding-bottom:10px;margin-bottom:14px;
              display:flex;justify-content:space-between;align-items:flex-end;">
    <div>
      <div style="font-size:18pt;font-weight:bold;color:#1e3a5f;">Conferência de Caixa</div>
      <div style="font-size:12pt;color:#555;margin-top:2px;">
        Turno #${turno.id} — ${modoResumo === 'detalhado' ? 'Detalhado' : 'Resumido'}
      </div>
    </div>
    <div style="text-align:right;font-size:11pt;color:#555;line-height:1.7;">
      <div><strong>Operador:</strong> ${turno.usuario_nome}</div>
      ${turno.fechamento_em && turno.fechado_por_nome && turno.fechado_por_nome !== turno.usuario_nome
        ? `<div><strong>Fechado por:</strong> ${turno.fechado_por_nome}</div>` : ''}
      ${turno.conta_nome ? `<div><strong>Conta:</strong> ${turno.conta_nome}</div>` : ''}
      <div><strong>Abertura:</strong> ${fmtDT(turno.abertura_em)}</div>
      ${turno.fechamento_em ? `<div><strong>Fechamento:</strong> ${fmtDT(turno.fechamento_em)}</div>` : '<div style="color:#b45309;"><strong>Status:</strong> Turno em aberto</div>'}
    </div>
  </div>

  <!-- Cards de resumo de vendas -->
  <div style="display:flex;gap:10px;margin-bottom:14px;">
    <div style="background:#1e3a5f;color:#fff;border-radius:6px;padding:10px 16px;text-align:center;flex:1;">
      <div style="font-size:10pt;opacity:.8;margin-bottom:4px;">Vendas</div>
      <div style="font-size:20pt;font-weight:bold;">${totais?.qtd_vendas || 0}</div>
    </div>
    <div style="background:#065f46;color:#fff;border-radius:6px;padding:10px 16px;text-align:center;flex:2;">
      <div style="font-size:10pt;opacity:.8;margin-bottom:4px;">Total Vendas</div>
      <div style="font-size:20pt;font-weight:bold;">R$ ${fmtBRL(totais?.total_vendas)}</div>
    </div>
    <div style="background:#7f1d1d;color:#fff;border-radius:6px;padding:10px 16px;text-align:center;flex:1;">
      <div style="font-size:10pt;opacity:.8;margin-bottom:4px;">Descontos</div>
      <div style="font-size:20pt;font-weight:bold;">R$ ${fmtBRL(totais?.total_descontos)}</div>
    </div>
  </div>

  <!-- Conferência de Caixa (Dinheiro) -->
  <div style="border:1px solid #bfdbfe;border-radius:8px;padding:14px;background:#eff6ff;margin-bottom:14px;">
    <div style="font-size:12pt;font-weight:bold;color:#1e40af;margin-bottom:10px;">
      Conferência de Caixa (Dinheiro)
    </div>
    <div style="display:flex;gap:10px;margin-bottom:${diferenca_resumo !== null ? '10px' : '0'};">
      ${cardHtml('Saldo Abertura', turno.saldo_abertura, '#1e3a5f')}
      ${cardHtml('Vendas em Dinheiro', dinheiro_resumo, '#065f46')}
      ${cardHtml('Valor em Caixa (calculado)', valor_em_caixa_resumo, '#1e40af', true)}
      ${turno.saldo_fechamento != null
        ? cardHtml('Saldo Fechamento (informado)', turno.saldo_fechamento, '#555')
        : `<div style="background:#f0f4fa;border:1px solid #c5d5e8;border-radius:6px;
                       padding:10px 14px;text-align:center;flex:1;min-width:130px;opacity:.4;">
             <div style="font-size:10pt;color:#64748b;margin-bottom:4px;">Saldo Fechamento</div>
             <div style="font-size:15pt;color:#555;">—</div>
           </div>`}
    </div>
    ${diferenca_resumo !== null ? `
    <div style="padding:10px 14px;border-radius:6px;background:${difBg};
                display:flex;justify-content:space-between;align-items:center;">
      <div>
        <div style="font-size:11pt;color:#444;">
          Diferença = Saldo Fechamento − Valor em Caixa Calculado
        </div>
        <div style="font-size:10pt;color:#888;margin-top:2px;">
          R$ ${fmtBRL(turno.saldo_fechamento)} − R$ ${fmtBRL(valor_em_caixa_resumo)}
        </div>
      </div>
      <div style="font-size:18pt;font-weight:bold;color:${difCor};">
        ${diferenca_resumo >= 0 ? '+' : ''}R$ ${fmtBRL(diferenca_resumo)}
        <span style="font-size:11pt;font-weight:normal;margin-left:6px;">${difLabel}</span>
      </div>
    </div>` : ''}
  </div>

  <!-- Formas de pagamento -->
  <div style="margin-bottom:14px;">
    <div style="font-size:12pt;font-weight:bold;color:#1e3a5f;border-bottom:2px solid #1e3a5f;
                padding-bottom:4px;margin-bottom:8px;">Resumo por Forma de Pagamento</div>
    <table style="width:100%;border-collapse:collapse;">
      <thead>
        <tr>
          <th style="${thStyle}">Forma de Pagamento</th>
          <th style="${thStyle}">Bandeira</th>
          <th style="${thStyle}text-align:center;">Qtd</th>
          <th style="${thStyle}text-align:right;">Total (R$)</th>
        </tr>
      </thead>
      <tbody>${linhasFormas}</tbody>
      <tfoot>
        <tr style="background:#1e3a5f;color:#fff;font-weight:bold;">
          <td style="padding:6px 8px;font-size:11pt;" colspan="2">Total Geral</td>
          <td style="padding:6px 8px;font-size:11pt;text-align:center;">
            ${formas.reduce((a, f) => a + parseInt(f.qtd), 0)}
          </td>
          <td style="padding:6px 8px;font-size:11pt;text-align:right;">
            R$ ${fmtBRL(formas.reduce((a, f) => a + parseFloat(f.total), 0))}
          </td>
        </tr>
      </tfoot>
    </table>
  </div>

  ${linhasMovs}
  ${linhasVendas}

  <!-- Rodapé -->
  <div style="margin-top:20px;border-top:1px solid #e5e7eb;padding-top:8px;
              display:flex;justify-content:space-between;font-size:10pt;color:#888;">
    <span>Emitido em ${new Date().toLocaleString('pt-BR')}</span>
    <span>Turno #${turno.id}</span>
  </div>
</body>
</html>`;

    const win = window.open('', '_blank', 'width=850,height=750');
    if (!win) { alert('Permita pop-ups para imprimir.'); return; }
    win.document.write(html);
    win.document.close();
    fecharComEsc(win);
    win.focus();
    setTimeout(() => win.print(), 400);
  };

  // Relatório de fechamento pra impressora térmica (58mm/80mm) — mesma
  // linguagem visual do cupom de venda, formato enxuto pensado pra conferir
  // caixa rápido (sem a versão detalhada por venda, que é só a A4).
  const imprimirResumoTermico = () => imprimirFechamentoCaixaTermico(resumo, larguraTermica);

  return (
    <div className={s.erpWrap}>
      <div className={s.titleBar}><span>🏧</span> Fechamento de Caixa</div>

      <div className={s.toolbar}>
        <button className={`${s.btnErp} ${aba==='turno'?s.btnErpPrimario:''}`}
          onClick={() => setAba('turno')}>Turno Atual</button>
        <button className={`${s.btnErp} ${aba==='historico'?s.btnErpPrimario:''}`}
          onClick={() => { setAba('historico'); setResumo(null); setTurnoSel(null); }}>Histórico</button>
        {resumo && (
          <>
            <span style={{ fontSize: 12, color: '#555', marginLeft: 8 }}>Visualização:</span>
            <button className={`${s.btnErp} ${modoResumo==='resumido'?s.btnErpPrimario:''}`}
              onClick={() => setModoResumo('resumido')}>Resumido</button>
            <button className={`${s.btnErp} ${modoResumo==='detalhado'?s.btnErpPrimario:''}`}
              onClick={() => setModoResumo('detalhado')}>Detalhado</button>
            <button className={s.btnErp} onClick={imprimirResumo} style={{ marginLeft: 8 }}>🖨 Imprimir</button>
            <select className={s.select} value={larguraTermica}
              onChange={e => setLarguraTermica(e.target.value)}
              style={{ marginLeft: 8, height: 30, fontSize: 12 }}
              title="Largura da impressora térmica">
              <option value="58mm">58mm</option>
              <option value="80mm">80mm</option>
            </select>
            <button className={s.btnErp} onClick={imprimirResumoTermico}>🖨 Imprimir Térmica</button>
          </>
        )}
      </div>

      {erro    && <div className={s.erroMsg}>{erro}</div>}
      {sucesso && <div className={s.sucessoMsg}>{sucesso}</div>}

      {aba === 'turno' && (
        <div className={s.conteudo}>
          {carregando
            ? <div style={{ color: '#888', fontSize: 12 }}>Carregando...</div>
            : !turnoAberto
              ? (
                <div style={{ maxWidth: 400 }}>
                  <div className={s.fieldset}>
                    <div className={s.legend}>Abertura de Caixa</div>
                    <div className={s.formGrupo} style={{ marginBottom: 8 }}>
                      <label className={s.label}>Conta de Caixa</label>
                      <select className={`${s.select} ${s.inputLarge}`} value={contaAbertura}
                        onChange={e => setContaAbertura(e.target.value)}>
                        <option value="">— selecione (opcional) —</option>
                        {contas.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                      </select>
                    </div>
                    <div className={s.formGrupo} style={{ marginBottom: 12 }}>
                      <label className={s.label}>Saldo de Abertura (R$)</label>
                      <input className={s.input} type="number" step="0.01" value={saldoAbertura}
                        onChange={e => setSaldoAbertura(e.target.value)} style={{ width: 130 }} />
                    </div>
                    <button className={s.btnErpPrimario} onClick={abrirTurno}>
                      Abrir Caixa
                    </button>
                  </div>
                </div>
              )
              : (
                <div style={{ maxWidth: 560 }}>
                  {/* Info do turno */}
                  <div className={s.fieldset} style={{ marginBottom: 12 }}>
                    <div className={s.legend}>Turno em Andamento</div>
                    <div style={{ fontSize: 12, lineHeight: 1.8 }}>
                      <div><strong>Abertura:</strong> {fmtDT(turnoAberto.abertura_em)}</div>
                      {turnoAberto.conta_nome && <div><strong>Conta:</strong> {turnoAberto.conta_nome}</div>}
                      <div><strong>Saldo abertura:</strong> R$ {fmtBRL(turnoAberto.saldo_abertura)}</div>
                    </div>
                    <button className={s.btnErp} style={{ marginTop: 8 }}
                      onClick={() => verResumo(turnoAberto)}>
                      Ver Resumo Atual
                    </button>
                  </div>

                  {/* Painel de cálculo em tempo real */}
                  <div style={{
                    border: '1px solid #bfdbfe', borderRadius: 8, padding: 14,
                    background: '#eff6ff', marginBottom: 12,
                  }}>
                    <div style={{ fontSize: 12, fontWeight: 'bold', color: '#1e40af', marginBottom: 10 }}>
                      Posição do Caixa (atualizado)
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 }}>
                      {[
                        { l: 'Saldo Abertura',      v: turnoAberto.saldo_abertura, cor: '#1e40af' },
                        { l: 'Vendas em Dinheiro',  v: dinheiro_atual,             cor: '#065f46' },
                        { l: 'Movimentações (±)',   v: movs_atual_ajuste,          cor: movs_atual_ajuste < 0 ? '#b91c1c' : '#1e40af' },
                        { l: 'Valor em Caixa',      v: valor_em_caixa,             cor: '#1e40af', bold: true },
                      ].map(({ l, v, cor, bold }) => (
                        <div key={l} style={{
                          background: '#fff', border: '1px solid #bfdbfe',
                          borderRadius: 6, padding: '8px 12px', textAlign: 'center',
                        }}>
                          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>{l}</div>
                          <div style={{ fontSize: 15, fontWeight: bold ? 'bold' : '600', color: cor }}>
                            R$ {fmtBRL(v)}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Diferença em tempo real */}
                    {diferenca_atual !== null && (
                      <div style={{
                        marginTop: 10, padding: '8px 14px', borderRadius: 6,
                        background: bgDiferenca(diferenca_atual),
                        border: `1px solid ${corDiferenca(diferenca_atual)}30`,
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      }}>
                        <span style={{ fontSize: 12, color: '#444' }}>
                          Diferença (informado − calculado):
                        </span>
                        <strong style={{ fontSize: 15, color: corDiferenca(diferenca_atual) }}>
                          {diferenca_atual >= 0 ? '+' : ''}R$ {fmtBRL(diferenca_atual)}
                          {' '}
                          <span style={{ fontSize: 11, fontWeight: 'normal' }}>
                            {Math.abs(diferenca_atual) < 0.005 ? '(conferido)' : diferenca_atual > 0 ? '(sobra)' : '(falta)'}
                          </span>
                        </strong>
                      </div>
                    )}
                  </div>

                  {/* Fechar caixa */}
                  <div className={s.fieldset}>
                    <div className={s.legend}>Fechar Caixa</div>
                    <div className={s.formRow}>
                      <div className={s.formGrupo}>
                        <label className={s.label}>Saldo de Fechamento (R$) *</label>
                        <input className={s.input} type="number" step="0.01"
                          value={saldoFechamento} onChange={e => setSaldoFechamento(e.target.value)}
                          style={{ width: 160 }} placeholder="0,00" autoFocus />
                        <span style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                          Valor contado fisicamente no caixa
                        </span>
                      </div>
                      <div className={s.formGrupo} style={{ flex: 1 }}>
                        <label className={s.label}>Observações</label>
                        <input className={`${s.input} ${s.inputLarge}`} value={obsFechar}
                          onChange={e => setObsFechar(e.target.value)} placeholder="Opcional..." />
                      </div>
                    </div>
                    <button className={s.btnPerigo} style={{ marginTop: 8 }} onClick={fecharTurno}>
                      Fechar Caixa
                    </button>
                  </div>
                </div>
              )
          }
        </div>
      )}

      {aba === 'historico' && !turnoSel && (
        <div className={s.conteudo}>
          <div className={s.secaoTitulo}>Histórico de Turnos</div>
          {/* Filtro por período (data de abertura) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0 12px', flexWrap: 'wrap' }}>
            <label style={{ fontSize: 12 }}>Período:</label>
            <InputDataFiltro className={s.inputErp} style={{ width: 140 }}
              value={histDe} onChange={e => setHistDe(e.target.value)} />
            <span style={{ fontSize: 12 }}>até</span>
            <InputDataFiltro className={s.inputErp} style={{ width: 140 }}
              value={histAte} onChange={e => setHistAte(e.target.value)} />
            <button className={s.btnErp} title="Filtrar turnos pelo período de abertura"
              onClick={async () => {
                try {
                  const { data } = await api.get('/financeiro/turnos',
                    { params: { de: histDe || undefined, ate: histAte || undefined } });
                  setHistorico(data);
                } catch { setErro('Erro ao filtrar turnos.'); }
              }}>Filtrar</button>
            <button className={s.btnErp}
              onClick={async () => {
                setHistDe(''); setHistAte('');
                try { const { data } = await api.get('/financeiro/turnos'); setHistorico(data); }
                catch { /* silencioso */ }
              }}>Limpar</button>
          </div>
          {historico.length === 0
            ? <div style={{ color: '#888', fontSize: 12 }}>Nenhum turno registrado.</div>
            : (
              <table className={s.tabela}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Operador</th>
                    <th>Fechado por</th>
                    <th>Conta</th>
                    <th>Abertura</th>
                    <th>Fechamento</th>
                    <th>Saldo Abertura</th>
                    <th>Saldo Fechamento</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {historico.map(t => (
                    <tr key={t.id}>
                      <td>{t.id}</td>
                      <td>{t.usuario_nome}</td>
                      <td>{t.fechamento_em ? (t.fechado_por_nome || t.usuario_nome) : '—'}</td>
                      <td>{t.conta_nome || '—'}</td>
                      <td>{fmtDT(t.abertura_em)}</td>
                      <td>{t.fechamento_em ? fmtDT(t.fechamento_em) : '—'}</td>
                      <td style={{ textAlign: 'right' }}>R$ {fmtBRL(t.saldo_abertura)}</td>
                      <td style={{ textAlign: 'right' }}>
                        {t.saldo_fechamento != null ? `R$ ${fmtBRL(t.saldo_fechamento)}` : '—'}
                      </td>
                      <td>
                        <span className={`${s.badge} ${t.status==='aberto'?s.badgeCaixa:s.badgeAtivo}`}>
                          {t.status === 'aberto' ? 'Aberto' : 'Fechado'}
                        </span>
                      </td>
                      <td>
                        <button className={s.btnErp} style={{ fontSize: 11, height: 20, padding: '0 6px' }}
                          onClick={() => verResumo(t)}>Resumo</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          }
        </div>
      )}

      {aba === 'historico' && turnoSel && resumo && (
        <div className={s.conteudo}>
          <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
            <button className={s.btnErp} onClick={() => { setTurnoSel(null); setResumo(null); }}>
              ← Voltar
            </button>
            <strong>Turno #{turnoSel.id} — {resumo.turno.usuario_nome}</strong>
            {resumo.turno.fechamento_em && resumo.turno.fechado_por_nome
              && resumo.turno.fechado_por_nome !== resumo.turno.usuario_nome && (
              <span style={{ fontSize: 12, color: '#92400e', background: '#fffbeb',
                border: '1px solid #f59e0b', borderRadius: 4, padding: '2px 8px' }}>
                Fechado por {resumo.turno.fechado_por_nome}
              </span>
            )}
            <span style={{ fontSize: 12, color: '#888' }}>
              {fmtDT(resumo.turno.abertura_em)}
              {resumo.turno.fechamento_em ? ` → ${fmtDT(resumo.turno.fechamento_em)}` : ' (aberto)'}
            </span>
          </div>

          {/* Cards de totais */}
          <div className={s.totaisRow}>
            <div className={s.totalCard}>
              <div className={s.valor}>{resumo.totais?.qtd_vendas || 0}</div>
              <div className={s.rotulo}>Vendas</div>
            </div>
            <div className={`${s.totalCard} ${s.totalCardVerde}`}>
              <div className={s.valor}>R$ {fmtBRL(resumo.totais?.total_vendas)}</div>
              <div className={s.rotulo}>Total Vendas</div>
            </div>
            <div className={`${s.totalCard} ${s.totalCardVermelho}`}>
              <div className={s.valor}>R$ {fmtBRL(resumo.totais?.total_descontos)}</div>
              <div className={s.rotulo}>Descontos</div>
            </div>
          </div>

          {/* Painel de conferência de caixa */}
          <div style={{
            border: '1px solid #bfdbfe', borderRadius: 8, padding: 16,
            background: '#eff6ff', marginBottom: 16,
          }}>
            <div style={{ fontSize: 13, fontWeight: 'bold', color: '#1e40af', marginBottom: 12 }}>
              Conferência de Caixa (Dinheiro)
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 10 }}>
              {[
                { l: 'Saldo Abertura',           v: resumo.turno.saldo_abertura, cor: '#1e40af' },
                { l: 'Vendas em Dinheiro',        v: dinheiro_resumo,            cor: '#065f46' },
                {
                  l: 'Valor em Caixa (calculado)',
                  v: valor_em_caixa_resumo,
                  cor: '#1e40af', bold: true,
                  title: 'Saldo abertura + vendas em dinheiro',
                },
                {
                  l: 'Saldo Fechamento (informado)',
                  v: resumo.turno.saldo_fechamento ?? 0,
                  cor: '#555',
                  faded: resumo.turno.saldo_fechamento == null,
                },
              ].map(({ l, v, cor, bold, title, faded }) => (
                <div key={l} title={title} style={{
                  background: '#fff', border: '1px solid #bfdbfe',
                  borderRadius: 6, padding: '8px 12px', textAlign: 'center',
                  opacity: faded ? 0.45 : 1,
                }}>
                  <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>{l}</div>
                  <div style={{ fontSize: 15, fontWeight: bold ? 'bold' : '600', color: cor }}>
                    {faded ? '—' : `R$ ${fmtBRL(v)}`}
                  </div>
                </div>
              ))}
            </div>

            {/* Diferença */}
            {diferenca_resumo !== null && (
              <div style={{
                marginTop: 12, padding: '10px 16px', borderRadius: 6,
                background: bgDiferenca(diferenca_resumo),
                border: `1px solid ${corDiferenca(diferenca_resumo)}40`,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <div>
                  <div style={{ fontSize: 12, color: '#444' }}>
                    Diferença = Saldo Fechamento − Valor em Caixa Calculado
                  </div>
                  <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                    R$ {fmtBRL(resumo.turno.saldo_fechamento)} − R$ {fmtBRL(valor_em_caixa_resumo)}
                  </div>
                </div>
                <strong style={{ fontSize: 18, color: corDiferenca(diferenca_resumo) }}>
                  {diferenca_resumo >= 0 ? '+' : ''}R$ {fmtBRL(diferenca_resumo)}
                  <span style={{ fontSize: 12, fontWeight: 'normal', marginLeft: 6 }}>
                    {Math.abs(diferenca_resumo) < 0.005
                      ? '✔ conferido'
                      : diferenca_resumo > 0 ? '▲ sobra' : '▼ falta'}
                  </span>
                </strong>
              </div>
            )}
            {diferenca_resumo === null && (
              <div style={{ marginTop: 10, fontSize: 12, color: '#888', fontStyle: 'italic' }}>
                Turno ainda aberto — saldo de fechamento não informado.
              </div>
            )}
          </div>

          {/* Formas de pagamento */}
          <div className={s.secao}>
            <div className={s.secaoTitulo}>Resumo por Forma de Pagamento</div>
            <table className={s.tabela} style={{ maxWidth: 600 }}>
              <thead>
                <tr>
                  <th>Forma</th>
                  <th>Bandeira</th>
                  <th>Qtd</th>
                  <th>Total (R$)</th>
                </tr>
              </thead>
              <tbody>
                {resumo.formas.map((f, i) => {
                  const isDin    = f.forma_pagamento?.toLowerCase() === 'dinheiro';
                  // f.total (dinheiro) já é líquido — soma de volta o troco só pra
                  // exibir o bruto recebido, coerente com a linha "(−) Troco" abaixo.
                  const totalLiq = isDin ? parseFloat(f.total) + troco_resumo : parseFloat(f.total);
                  return (
                    <React.Fragment key={i}>
                      <tr style={isDin ? { background: '#f0fdf4', fontWeight: 'bold' } : {}}>
                        <td>{f.forma_pagamento}</td>
                        <td>{f.bandeira || '—'}</td>
                        <td style={{ textAlign: 'center' }}>{f.qtd}</td>
                        <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                          R$ {fmtBRL(totalLiq)}
                        </td>
                      </tr>
                      {isDin && troco_resumo > 0 && (
                        <tr style={{ color: '#b45309', fontSize: 11 }}>
                          <td colSpan={3} style={{ paddingLeft: 20 }}>(−) Troco concedido</td>
                          <td style={{ textAlign: 'right' }}>R$ {fmtBRL(troco_resumo)}</td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Baixas pagas com crédito do cliente (não movimentam dinheiro) */}
          {parseFloat(resumo.creditos_uso?.total || 0) > 0 && (
            <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6,
              padding: '8px 14px', margin: '8px 0', fontSize: 13, color: '#166534', maxWidth: 700 }}
              title="Contas a receber baixadas usando crédito de devolução — informativo: não entra dinheiro no caixa">
              💳 <strong>Baixas com crédito do cliente:</strong>{' '}
              R$ {fmtBRL(resumo.creditos_uso.total)} em {resumo.creditos_uso.qtd} baixa(s)
              <span style={{ color: '#666', marginLeft: 8, fontSize: 11 }}>(não movimenta o caixa)</span>
            </div>
          )}

          {/* Movimentações do turno */}
          {resumo.movs.length > 0 && (
            <div className={s.secao}>
              <div className={s.secaoTitulo}>Movimentações</div>
              <table className={s.tabela} style={{ maxWidth: 700 }}>
                <thead>
                  <tr><th>Tipo</th><th>Origem</th><th>Destino</th><th>Valor</th><th>Descrição</th><th></th></tr>
                </thead>
                <tbody>
                  {resumo.movs.map((m, i) => (
                    <tr key={i}>
                      <td>{m.tipo}</td>
                      <td>{m.conta_origem_nome || '—'}</td>
                      <td>{m.conta_destino_nome || '—'}</td>
                      <td style={{ textAlign: 'right' }}>R$ {fmtBRL(m.valor)}</td>
                      <td>{m.descricao || '—'}</td>
                      <td style={{ textAlign: 'center' }}>
                        {m.tipo === 'sangria' && (
                          <button className={s.btnErp} style={{ padding: '1px 6px', fontSize: 11 }}
                            title="Imprimir comprovante da sangria"
                            onClick={() => imprimirComprovanteMovimentacao(m)}>🖨️</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Detalhado — agrupado por forma de pagamento */}
          {modoResumo === 'detalhado' && resumo.vendas.length > 0 && (() => {
            const map = {};
            resumo.vendas.forEach(v => {
              const trocoV = parseFloat(v.troco || 0);
              (v.pagamentos || []).forEach(p => {
                const isDin = p.forma?.toLowerCase() === 'dinheiro';
                const key   = `${p.forma}${p.bandeira ? ` (${p.bandeira})` : ''}`;
                if (!map[key]) map[key] = { label: key, isDin, itens: [], total: 0 };
                // p.valor já é líquido (troco descontado no backend) — a coluna
                // "Troco" ao lado é só informativa, não precisa subtrair de novo.
                const valorLiq = parseFloat(p.valor || 0);
                map[key].itens.push({ v, p, trocoV: isDin ? trocoV : 0, valorLiq });
                map[key].total += valorLiq;
              });
            });
            return (
              <div className={s.secao}>
                <div className={s.secaoTitulo}>Vendas Detalhadas por Forma de Pagamento</div>
                {Object.values(map).map(g => (
                  <div key={g.label} style={{ marginBottom: 20 }}>
                    <div style={{
                      fontSize: 12, fontWeight: 'bold', color: '#374151',
                      borderBottom: '1px solid #d1d5db',
                      paddingBottom: 4, marginBottom: 6,
                      display: 'flex', justifyContent: 'space-between',
                    }}>
                      <span>{g.label}</span>
                      <span style={{ fontWeight: 'normal', color: '#6b7280' }}>
                        {g.itens.length} venda(s)
                      </span>
                    </div>
                    {/* table-layout:fixed + larguras próprias (em vez do .tabela
                        padrão, que estica Cliente/Vendedor até preencher 100% da
                        tela) — sobra espaço pra coluna Obs sem virar uma grade
                        cheia de vão vazio entre as colunas. */}
                    <table className={s.tabela} style={{ tableLayout: 'fixed', width: '100%' }}>
                      <colgroup>
                        <col style={{ width: 46 }} />
                        <col style={{ width: 110 }} />
                        <col style={{ width: '18%' }} />
                        <col style={{ width: '12%' }} />
                        <col style={{ width: '22%' }} />
                        <col style={{ width: 85 }} />
                        {g.isDin && <col style={{ width: 75 }} />}
                        <col style={{ width: 95 }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Data/Hora</th>
                          <th>Cliente</th>
                          <th>Vendedor</th>
                          <th>Obs</th>
                          <th style={{ textAlign: 'right' }}>Desconto</th>
                          {g.isDin && <th style={{ textAlign: 'right' }}>Troco</th>}
                          <th style={{ textAlign: 'right' }}>{g.isDin ? 'Líquido' : 'Valor'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.itens.map(({ v, p, trocoV, valorLiq }, i) => (
                          <tr key={i}>
                            <td>{v.id}</td>
                            <td>{fmtDT(v.criado_em)}</td>
                            <td style={{ overflow: 'hidden', textOverflow: 'ellipsis' }} title={v.cliente_nome || 'Consumidor Final'}>
                              {v.cliente_nome || 'Consumidor Final'}
                            </td>
                            <td style={{ overflow: 'hidden', textOverflow: 'ellipsis' }} title={v.vendedor_nome || '—'}>
                              {v.vendedor_nome || '—'}
                            </td>
                            <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', color: '#6b7280' }}
                              title={v.observacoes || ''}>
                              {v.observacoes || '—'}
                            </td>
                            <td style={{ textAlign: 'right', color: '#b91c1c' }}>
                              {parseFloat(v.desconto) > 0 ? `R$ ${fmtBRL(v.desconto)}` : '—'}
                            </td>
                            {g.isDin && (
                              <td style={{ textAlign: 'right', color: '#b45309' }}>
                                {trocoV > 0 ? `R$ ${fmtBRL(trocoV)}` : '—'}
                              </td>
                            )}
                            <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                              R$ {fmtBRL(valorLiq)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ borderTop: '1px solid #d1d5db' }}>
                          <td colSpan={g.isDin ? 7 : 6} style={{ padding: '4px 8px', fontSize: 12,
                            fontWeight: 'bold', color: '#374151' }}>
                            Total {g.label}
                          </td>
                          <td style={{ textAlign: 'right', padding: '4px 8px',
                            fontWeight: 'bold', fontSize: 13, color: '#111827' }}>
                            R$ {fmtBRL(g.total)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
